<!-- eslint-disable vue/no-v-html -->
<template>
    <div>
        <v-container grid-list-xs>
            <v-sheet class="pa-5" elevation="3">
                <Form ref="formRef" :messages="messages" :validations="validations" @submit="submit">
                    <v-row>
                        <v-col>
                            <PageHeader class="header">
                                <template #header>
                                    <h4>{{ $t("additionalInfo.heading") }}</h4>
                                </template>
                                <template #subheader>
                                    {{ $t("additionalInfo.description") }}
                                </template>
                            </PageHeader>
                        </v-col>
                    </v-row>

                    <div class="pt-sm-5">
                        <v-subheader class="input-title">{{ $t("additionalInfo.payment-frequency") }}</v-subheader>
                        <p>{{ $t("additionalInfo.payment-frequency-description") }}</p>
                        <v-row dense>
                            <v-col>
                                <AppField
                                    v-if="fields['primaryIncomePayFrequency']"
                                    :application="application"
                                    :field="fields['primaryIncomePayFrequency']"
                                    type="select"
                                    label-code="field.payFrequency"
                                    :form-settings="formSettings"
                                    append-icon="$chevronDown"
                                    :required="true"
                                >
                                </AppField>
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="12" sm="6">
                                <AppField
                                    v-if="fields['primaryIncomePreviousPayDate']"
                                    :application="application"
                                    :field="fields['primaryIncomePreviousPayDate']"
                                    label-code="field.previousPayDate"
                                    type="date"
                                    :form-settings="formSettings"
                                    :max="today"
                                    :required="true"
                                />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <AppField
                                    v-if="fields['primaryIncomeNextPayDate']"
                                    :application="application"
                                    :field="fields['primaryIncomeNextPayDate']"
                                    label-code="field.nextPayDate"
                                    type="date"
                                    :form-settings="formSettings"
                                    :min="today"
                                    :required="true"
                                />
                            </v-col>
                        </v-row>
                    </div>

                    <div v-min-children="2">
                        <v-subheader class="input-title">Contact</v-subheader>
                        <v-row v-min-children="1" dense>
                            <v-col v-min-children="1" cols="12" lg="6">
                                <AppField
                                    v-if="fields['primaryMobilePhone']"
                                    :application="application"
                                    :field="fields['primaryMobilePhone']"
                                    label-code="field.mobilePhone"
                                    type="phone"
                                    :form-settings="formSettings"
                                    :required="true"
                                />
                            </v-col>
                        </v-row>
                        <v-row v-min-children="1" dense>
                            <v-col v-min-children="1" cols="12" lg="6">
                                <AppField
                                    v-if="fields['primaryEmail']"
                                    ref="primaryEmailField"
                                    :application="application"
                                    :field="fields['primaryEmail']"
                                    label-code="field.email"
                                    :form-settings="formSettings"
                                    :make-optional="false"
                                    type="email"
                                    messages="Email is required to determine eligibility for this offer."
                                    :required="true"
                                    @input="emailChanged"
                                />
                            </v-col>
                        </v-row>
                    </div>

                    <div>
                        <CheckboxField id="arbitration-checkbox" v-model="arbitrationCheckbox" required>
                            <a id="arbitration-link" class="text-decoration-underline" @click="toggleArbitration">
                                Arbitration Provision
                            </a>
                        </CheckboxField>

                        <v-row v-show="arbitrationDisplayed">
                            <v-col
                                v-if="arbitrationHtml"
                                id="arbitration-html-parent"
                                class="ml-4 grey-darken-2 arbitration-parent"
                            >
                                <p>
                                    <SafeHtmlRenderer :content="arbitrationHtml" tag="span"/>
                                </p>
                            </v-col>
                        </v-row>
                    </div>

                    <div>
                        <CheckboxField id="terms-checkbox" v-model="termsCheckbox" required>
                            <a class="text-decoration-underline" @click="toggleTerms"> Disclosure & Disclaimer </a>
                        </CheckboxField>

                        <v-row v-show="termsDisplayed">
                            <v-col v-show="termsType === 'html'" class="ml-4 grey-darken-2">
                                <p>
                                    <SafeHtmlRenderer :content="termsHtml" tag="span"/>
                                </p>
                            </v-col>
                        </v-row>
                    </div>

                    <div>
                        <CheckboxField id="privacy-checkbox" v-model="privacyCheckbox" required>
                            <a id="privacy-link" class="text-decoration-underline" @click="togglePrivacyPolicy">
                                Privacy Policy
                            </a>
                        </CheckboxField>
                    </div>

                    <div>
                        <CheckboxField id="tcpa-checkbox" v-model="tcpaCheckbox">
                            <a class="text-decoration-underline" @click="toggleTcpa">
                                Consent to Receive Marketing Calls And Texts (Optional)
                            </a>
                        </CheckboxField>

                        <v-row v-if="tcpaDisplayed">
                            <v-col class="ml-4 grey-darken-2">
                                <p>
                                    <SafeHtmlRenderer :content="tcpa"  tag="span"/>
                                </p>
                            </v-col>
                        </v-row>
                    </div>

                    <v-row>
                        <v-col>
                            <p>{{ $t("terms.consent-directions") }}</p>
                        </v-col>
                    </v-row>

                    <div class="card-actions pb-12 mt-8">
                        <BackButton v-if="showBackButton" :to="backPath" class="card-actions-back" color="primary" />
                        <SubmitButton class="card-actions-submit secondary" :disabled="isDisabled">
                            Agree &amp; Submit
                        </SubmitButton>
                    </div>
                </Form>
                <v-row>
                    <v-container
                        v-if="
                            !primaryLenderStandardApp &&
                            primeProviderInfo &&
                            primeProviderInfo.name !== currentProviderInfo.name
                        "
                        id="standard-notification"
                        class="d-flex justify-lg-start align-center ma-4"
                    >
                        <v-icon color="secondary">$infoCircle</v-icon>
                        <p class="my-0 mx-2">
                            Sorry, we could not find an offer with {{ primeProviderInfo.name }} at this time, but you
                            can apply through their standard application
                            <router-link :to="standardRoute">here</router-link>
                        </p>
                    </v-container>
                </v-row>
            </v-sheet>
        </v-container>
    </div>
</template>

<script>
    import { applicationState, fieldState, providerState, sessionState } from "@/state"
    import { indexBy } from "@/util/array"
    import Providers from "@/model/provider/Providers"
    import ApplyMode from "@/model/apply/ApplyMode"
    import { payDateNotValidForFrequency } from "@/util/validation"
    import { applyService, googleAnalyticsService } from "@/service"
    import affValidationService from "@/service/provider/aff/AffValidationService"
    import GoogleAnalyticEvents from "@/model/google/GoogleAnalyticEvents"
    import SafeHtmlRenderer from "@/util/safeHtmlRenderer"

    export default {
        components: {
            SafeHtmlRenderer,
        },
        data() {
            return {
                disclosureMap: {},
                arbitrationDisplayed: false,
                termsDisplayed: false,
                tcpaDisplayed: false,
                privacyDisplayed: false,
                arbitrationCheckbox: false,
                termsCheckbox: false,
                privacyCheckbox: false,
                tcpaCheckbox: false,
                emailInputValid: true,
                emailTimeout: null,
                showBackButton: !sessionState.atHome,
                application: applicationState.application,
                currentProviderInfo: Providers[sessionState.dynamicProviderKey.providerKey],
                fields: indexBy(fieldState.fields, "id"),
                primeProviderInfo: Providers[providerState.primaryProvider.providerKey] || null,
                primaryLenderStandardApp: applicationState.primaryLenderApplyMode === ApplyMode.FULL,
                standardRoute: `/providers/${providerState.primaryProvider.providerKey}/modes/full/switch`,
                today: new Date(),
                formSettings: {
                    readOnly: false,
                },
                messages: {
                    en: {
                        rule: {
                            payDateNotValidForFrequency: "Next pay date not valid for pay frequency",
                            invalidEmail: "Email domain not supported",
                        },
                    },
                },
                validations: {
                    primaryIncomeNextPayDate: {
                        payDateNotValidForFrequency: this.calcPayDateNotValidForFrequency,
                    },

                    primaryEmail: {
                        invalidEmail: this.emailNotValid,
                    },
                },
            }
        },

        computed: {
            arbitrationType() {
                if (!this.disclosureMap["arbitration"]) {
                    return null
                }
                if (this.disclosureMap["arbitration"]?.contentType === "application/pdf") return "pdf"
                else if (this.disclosureMap["arbitration"]?.contentType === "text/html") return "html"
                else return null
            },

            backPath() {
                return `/providers/${sessionState.providerKey}/paymentPlans`
            },

            arbitrationHtml() {
                if (this.arbitrationType === "html") {
                    return this.disclosureMap["arbitration"]?.body
                }
                return null
            },

            arbitrationUrl() {
                if (this.arbitrationType === "pdf") {
                    return this.disclosureMap["arbitration"]?.body
                        ? this.disclosureMap["arbitration"]?.body
                        : this.disclosureMap["arbitration"].url
                }
                return null
            },

            termsType() {
                if (!this.disclosureMap["terms"]) {
                    return null
                }
                if (this.disclosureMap["terms"]?.contentType === "application/pdf") return "pdf"
                else if (this.disclosureMap["terms"]?.contentType === "text/html") return "html"
                else return null
            },

            termsHtml() {
                return this.disclosureMap["terms"]?.body
            },

            termsUrl() {
                return this.disclosureMap["terms"]?.url
            },

            tcpa() {
                return this.disclosureMap["tcpa"]?.body
            },

            privacyPolicy() {
                return this.disclosureMap["privacy-policy"]?.url
            },

            isDisabled() {
                return (
                    !this.application.primary.income.payFrequency ||
                    !this.application.primary.income.previousPayDate ||
                    !this.application.primary.income.nextPayDate ||
                    !this.application.primary.mobilePhone ||
                    !this.arbitrationCheckbox ||
                    !this.termsCheckbox ||
                    !this.privacyCheckbox ||
                    !this.application.primary.email ||
                    !this.emailInputValid
                )
            },
        },

        async mounted() {
            this.$refs.primaryEmailField.validate()
            await this.getDisclosures()
        },

        methods: {
            async getDisclosures() {
                const { disclosures } = await applyService.getDisclosures(sessionState.providerKey)
                this.disclosureMap = indexBy(disclosures, "type")
            },

            calcPayDateNotValidForFrequency(value, parentVM) {
                return payDateNotValidForFrequency(value, parentVM)
            },

            async emailNotValid(value) {
                const response = await affValidationService.validateEmail(value, sessionState.providerKey)
                this.emailInputValid = response.valid
                return response.valid
            },

            submit() {
                applicationState.application = this.application
                googleAnalyticsService.buttonEvent(
                    GoogleAnalyticEvents.ACCEPT_OFFER_SUBMITTED,
                    "Aff Accept Offer Submitted"
                )
                this.$router.push(`/providers/${sessionState.providerKey}/acceptOfferSubmit`)
            },

            // pdfable
            toggleArbitration() {
                if (this.arbitrationType === "pdf") {
                    this.$router.push(`/docs/pdf/${encodeURIComponent(this.arbitrationUrl)}`)
                } else {
                    this.arbitrationDisplayed = !this.arbitrationDisplayed
                }
            },

            // pdfable
            toggleTerms() {
                if (this.termsType === "pdf") {
                    this.$router.push(`/docs/pdf/${encodeURIComponent(this.termsUrl)}`)
                } else {
                    this.termsDisplayed = !this.termsDisplayed
                }
            },

            // pdf
            togglePrivacyPolicy() {
                this.$router.push(`/docs/pdf/${encodeURIComponent(this.privacyPolicy)}`)
            },

            // html, does not need to redirect
            toggleTcpa() {
                this.tcpaDisplayed = !this.tcpaDisplayed
            },

            emailChanged(value) {
                clearTimeout(this.emailTimeout)
                this.emailTimeout = setTimeout(() => {
                    this.$refs.primaryEmailField.validate()
                }, 1000)
            },
        },
    }
</script>

<style lang="scss" scoped>
    .v-btn-toggle--tile {
        margin: 0;
    }

    .header {
        min-height: 60px !important;
        @media #{map-get($display-breakpoints, "sm-and-up")} {
            height: 60px !important;
        }
    }

    .text-h5 {
        color: #007fcf;
    }

    .messages {
        font-size: 12px;
        margin-bottom: 0;
        margin-top: -10px;

        div {
            padding-left: 10px;
        }
    }

    .number-check {
        align-items: center;
    }

    .label {
        max-width: 26% !important;
    }

    .-ml-2 {
        margin-left: -10px;
    }

    .holder {
        display: flex;
        align-items: flex-start;
        align-content: flex-start;
        justify-content: center;
        margin-top: -30px;
        white-space: nowrap;
    }

    .what-is-this-link {
        font-weight: 600;
        font-size: 16px;
        line-height: 20px;
        text-decoration: none;
    }

    .salary-notice {
        margin-top: -50px;
        margin-bottom: -30px;
    }

    .theme--light.v-label {
        color: $grey-darken-2 !important;
    }

    mt-2 {
        margin-top: -10px !important;
    }

    .arbitration-pdf,
    .terms-pdf {
        width: 100%;
        border: 0;
    }

    .arbitration-parent {
        overflow: scroll;
        max-height: 800px;
        max-width: 100%;
    }
</style>
